import 'src/components/ui/combobox/style.scss';
import './style.scss';

import React, { useMemo, useRef, useState } from 'react';

export interface IDistributionMultiSelectOption {
    value: string | number;
    label: string;
}

interface IDistributionMultiSelectComboBoxProps {
    name: string;
    options: Array<string | IDistributionMultiSelectOption>;
    selectedOptions?: Array<string | number>;
    placeholder?: string;
    multipleValuesSelectedLabel?: string;
    onSelect?: (value: string | number) => void;
    disabled?: boolean;
    customClass?: string;
}

interface IDistributionOptionItemProps {
    parentName: string;
    optionName: string | number;
    optionLabel: string;
    index: number;
    onSelect?: (value: string | number) => void;
    selected: boolean;
    onOptionClick?: () => void;
}

const SINGLE_SELECTION = Number('1');

const DistributionOptionItem: React.FC<IDistributionOptionItemProps> = ({
    parentName,
    optionName,
    optionLabel,
    index,
    onSelect,
    selected,
    onOptionClick,
}) => {
    return (
        <div
            role='option'
            id={`${parentName}-option-${index}`}
            className='c-combobox__item combobox-item'
            data-value={`${optionLabel
                .toString()
                .replace(/ /, '_')
                .toLowerCase()}`}
            aria-selected={selected}
            aria-disabled='false'
            onClick={(event) => {
                event.stopPropagation();
                onOptionClick?.();
                onSelect?.(optionName);
            }}
        >
            <div className='c-combobox__item__text'>{optionLabel}</div>
        </div>
    );
};

const DistributionMultiSelectComboBox: React.FC<
    IDistributionMultiSelectComboBoxProps
> = ({
    name,
    options,
    selectedOptions = [],
    placeholder = '',
    multipleValuesSelectedLabel = 'Multiple values selected',
    onSelect,
    disabled = false,
    customClass = '',
}) => {
    const [visible, setVisible] = useState(false);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const normalizedOptions = useMemo(() => {
        return options.filter(Boolean).map((option) => {
            if (typeof option === 'string') {
                return {
                    value: option,
                    label: option,
                };
            }

            return option;
        });
    }, [options]);

    const displayedValue = () => {
        if (!selectedOptions.length) {
            return placeholder;
        }

        if (selectedOptions.length > SINGLE_SELECTION) {
            return multipleValuesSelectedLabel;
        }

        const selected = normalizedOptions.find(
            (option) => String(option.value) === String(selectedOptions[0]),
        );

        return selected?.label ?? String(selectedOptions[0]);
    };

    return (
        <div
            ref={wrapperRef}
            className={`c-combobox__wrapper js-combobox distribution-multiselect-combobox ${customClass}`}
        >
            <input
                type='hidden'
                name='combobox-values'
                className='js-combobox__input-values'
                disabled={disabled}
            ></input>
            <div
                role='combobox'
                aria-haspopup='listbox'
                aria-expanded={visible}
                aria-owns={`${name}-listbox`}
                aria-controls={`${name}-listbox`}
                aria-activedescendant=''
                tabIndex={0}
                id={name}
                className='c-combobox'
                aria-disabled={disabled}
                onClick={() => {
                    if (!disabled) {
                        setVisible((current) => !current);
                    }
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                        setVisible(false);
                    }
                }}
            >
                <div className='c-combobox__value-presentation js-combobox-value-presentation'>
                    <span className='c-combobox__value-presentation__text has-placeholder'>
                        {displayedValue()}
                    </span>
                </div>

                {visible && (
                    <div
                        className='c-combobox__listbox-container'
                        onMouseLeave={() => setVisible(false)}
                    >
                        <div
                            id={`${name}-listbox`}
                            className='c-combobox__listbox'
                            role='listbox'
                            aria-multiselectable='true'
                            aria-disabled='false'
                        >
                            {normalizedOptions.map((option, index) => {
                                const selected = selectedOptions.some(
                                    (selectedValue) =>
                                        String(selectedValue) ===
                                        String(option.value),
                                );

                                return (
                                    <DistributionOptionItem
                                        key={`${option.value}-${index}`}
                                        parentName={name}
                                        optionName={option.value}
                                        optionLabel={option.label}
                                        index={index}
                                        onSelect={onSelect}
                                        selected={selected}
                                        onOptionClick={() => {
                                            setVisible(true);
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DistributionMultiSelectComboBox;
